// Quote calculator + document generators for Drunk Fruit Event OS

export function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

const num = (v, d = 0) => {
  const x = Number(v);
  return Number.isNaN(x) ? d : x;
};

// Derive service hours from start/end time strings ("HH:MM"); handles midnight crossing.
export function hoursFromTimes(start, end) {
  if (!start || !end) return null;
  const p = (t) => {
    const parts = String(t).split(":");
    const h = Number(parts[0]);
    const m = Number(parts[1] || 0);
    if (Number.isNaN(h)) return null;
    return h * 60 + (Number.isNaN(m) ? 0 : m);
  };
  const s = p(start);
  const e = p(end);
  if (s == null || e == null) return null;
  let mins = e - s;
  if (mins <= 0) mins += 24 * 60; // crosses midnight
  return round2(mins / 60);
}

// Default line-item estimates from event data.
// Supplies & menu-specific ingredients both auto-calc as guests × hours × $1.25.
export function suppliesFormula(guests, hours) {
  return round2((Number(guests) || 0) * (Number(hours) || 0) * 1.25);
}
export function defaultBasicSupplies(event) {
  return suppliesFormula(event?.guest_count, event?.service_hours);
}
export function defaultMenuIngredients(event) {
  return suppliesFormula(event?.guest_count, event?.service_hours);
}

export const ADDON_PRESETS = [
  { label: "Personalized cups", details: "Event-specific add-on", regular: 70, customer: 70 },
  { label: "Smoked presentation", details: "Applewood aroma, tableside finish", regular: 70, customer: 70 },
  { label: "Custom menu sign", details: "Event-specific add-on", regular: 15, customer: 15 },
  { label: "Elaborate garnish design", details: "Premium garnish upgrade", regular: 60, customer: 60 },
  { label: "Glassware upgrade", details: "Corporate glassware service", regular: 200, customer: 200 },
  { label: "Kids lemonade bar", details: "Alcohol-free kids bar", regular: 120, customer: 120 },
  { label: "Extra service hour", details: "Per additional hour", regular: 75, customer: 75 },
  { label: "Extra bartender", details: "Additional bartender", regular: 300, customer: 300 },
];

// Core pricing engine — two columns (Regular Value vs Customer Price).
export function computeQuote(inp = {}, pricing = {}) {
  const bartenders = Math.max(1, num(inp.bartenders, 1));
  const hours = num(inp.hours, 0);
  const regRate = num(inp.regular_rate ?? pricing.regular_bartender_rate, 75) || 75;
  const rate = num(inp.rate ?? pricing.promo_bartender_rate, 55) || 55;

  const bartender_regular = round2(bartenders * hours * regRate);
  const bartender_customer = round2(bartenders * hours * rate);

  const mobile_bar_rental = num(inp.mobile_bar_rental, 0);
  const basic_supplies = num(inp.basic_supplies, 0);
  const menu_ingredients = num(inp.menu_ingredients, 0);
  const transportation = num(inp.transportation, 0);
  const discount = num(inp.discount, 0);
  const hst_rate = num(inp.hst_rate ?? pricing.hst_rate, 13);
  const deposit_pct = num(inp.deposit_pct ?? pricing.deposit_pct, 50);

  const addon_items = Array.isArray(inp.addon_items) ? inp.addon_items : [];
  const addons_total = round2(addon_items.reduce((s, a) => s + num(a.customer ?? a.price), 0));
  const addons_regular_total = round2(addon_items.reduce((s, a) => s + num(a.regular ?? a.customer ?? a.price), 0));

  const regular_price = round2(
    bartender_regular + mobile_bar_rental + basic_supplies + menu_ingredients + transportation + addons_regular_total
  );
  const customerBeforeDiscount =
    bartender_customer + mobile_bar_rental + basic_supplies + menu_ingredients + transportation + addons_total;
  const subtotal = round2(Math.max(0, customerBeforeDiscount - discount));
  const savings = round2(Math.max(0, regular_price - subtotal));
  const hst = round2(subtotal * (hst_rate / 100));
  const total = round2(subtotal + hst);
  const deposit = round2(total * (deposit_pct / 100));
  const balance = round2(total - deposit);

  return {
    bartenders, hours, rate, regular_rate: regRate,
    bartender_regular, bartender_customer,
    mobile_bar_rental, basic_supplies, menu_ingredients, transportation,
    addon_items, addons_total, addons_regular_total,
    discount, hst_rate, deposit_pct,
    regular_price, subtotal, savings, hst, total, deposit, balance,
    // legacy aliases so older views keep working
    promo_price: bartender_customer,
    supplies: round2(basic_supplies + menu_ingredients),
  };
}

// Validate that a quote can be generated with real numbers (never a silent $0).
export function validateQuoteInputs(event, inp = {}) {
  const missing = [];
  if (!event?.full_name) missing.push("client name");
  if (!event?.event_date) missing.push("event date");
  if (!(num(event?.guest_count) > 0)) missing.push("guest count");
  const hasHours = num(inp?.hours) > 0 || (event?.service_start_time && event?.service_end_time);
  if (!hasHours) missing.push("service hours");
  if (!(num(inp?.bartenders) >= 1)) missing.push("bartender count");
  const hasTransport = inp?.transportation != null && inp?.transportation !== "" && num(inp?.transportation) >= 0;
  if (!event?.event_city && !hasTransport) missing.push("city or transportation");
  if (!event?.package_type && !event?.service_type) missing.push("service type");
  return { valid: missing.length === 0, missing };
}

export function renderTemplate(text, event) {
  if (!text) return "";
  const q = event?.quote || {};
  const inv = event?.invoice || {};
  const map = {
    client_name: event?.full_name || "",
    event_date: event?.event_date || "",
    event_city: event?.event_city || "",
    guest_count: event?.guest_count ?? "",
    quote_total: q.total != null ? "$" + Number(q.total).toFixed(2) : "",
    deposit_amount: q.deposit != null ? "$" + Number(q.deposit).toFixed(2) : "",
    balance_due: q.balance != null ? "$" + Number(q.balance).toFixed(2) : "",
    payment_link: inv.square_link || "",
    quote_link: "", contract_link: "", invoice_link: "", menu_link: "", alcohol_list_link: "", selection_guide_link: "",
    service_start_time: event?.service_start_time || "",
    setup_arrival_time: event?.setup_arrival_time || "",
    selected_cocktails: (event?.selected_cocktails || []).join(", ") || event?.preferred_cocktails || "",
  };
  return text.replace(/\{(\w+)\}/g, (m, key) => (key in map ? String(map[key]) : m));
}

// ---- Alcohol list (CLIENT) — alcohol ONLY, from confirmed selected drinks ----
const SPIRIT_RULES = [
  { rx: /mezcal|oaxaca/, spirit: "Mezcal (750ml)" },
  { rx: /margarita|paloma|ranch water|cantarito|tequila|sunrise|mexican mule|tommy|siesta|el diablo|guava/, spirit: "Tequila (750ml)" },
  { rx: /mojito|daiquiri|colada|mai tai|hurricane|cuba libre|painkiller|bahama|blue hawaiian|dark and stormy|rum runner|jungle bird|\brum\b/, spirit: "White / Dark Rum (750ml)" },
  { rx: /cosmopolitan|cosmo|moscow mule|lemon drop|lychee|espresso martini|french martini|vodka|white russian|chocolate martini|caipiroska|screwdriver|blue lagoon|sex on the beach|pornstar|spritz peach/, spirit: "Vodka (750ml)" },
  { rx: /\bgin\b|tom collins|negroni|french 75|bramble|bee.?s knees|southside|aviation|clover club/, spirit: "Gin (750ml)" },
  { rx: /old fashioned|whiskey|whisky|bourbon|manhattan|gold rush|kentucky mule|paper plane|boulevardier|mint julep|new york sour/, spirit: "Whiskey / Bourbon (750ml)" },
  { rx: /sidecar|brandy|cognac|french connection/, spirit: "Brandy / Cognac (750ml)" },
];
const LIQUEUR_RULES = [
  { rx: /margarita|cosmopolitan|sidecar|cadillac|cointreau|triple sec/, item: "Triple Sec / Orange Liqueur (750ml)" },
  { rx: /espresso martini|white russian|irish coffee/, item: "Coffee Liqueur (750ml)" },
  { rx: /aperol/, item: "Aperol (750ml)" },
  { rx: /negroni|campari/, item: "Campari (750ml)" },
  { rx: /st.?germain|elderflower|hugo/, item: "Elderflower Liqueur (750ml)" },
];
const SPARKLING_RX = /spritz|mimosa|bellini|french 75|kir royale|prosecco|champagne|hugo/;
const WINE_RX = /sangria|\bwine\b/;
const BEER_RX = /michelada|\bbeer\b/;

export function generateClientAlcoholList(event) {
  const isMocktailOnly = String(event?.service_type || "").toLowerCase().includes("mocktail");
  const adults = num(event?.adult_count) || num(event?.guest_count);
  const hours = num(event?.service_hours) || 4;
  const drinks = Math.max(1, Math.round(adults * hours));
  const names = [...(event?.selected_cocktails || []), ...(event?.selected_mocktails || [])].map((s) =>
    String(s).toLowerCase()
  );

  const spirits = new Map();
  const liqueurs = new Set();
  let sparkling = false, wine = false, beer = false;
  names.forEach((nm) => {
    if (nm.includes("na") || nm.includes("virgin") || nm.includes("mocktail") || nm.includes("lemonade")) return;
    const s = SPIRIT_RULES.find((r) => r.rx.test(nm));
    if (s) spirits.set(s.spirit, (spirits.get(s.spirit) || 0) + 1);
    LIQUEUR_RULES.forEach((l) => { if (l.rx.test(nm)) liqueurs.add(l.item); });
    if (SPARKLING_RX.test(nm)) sparkling = true;
    if (WINE_RX.test(nm)) wine = true;
    if (BEER_RX.test(nm)) beer = true;
  });

  const cats = [];
  if (!isMocktailOnly && spirits.size) {
    const totalWeight = [...spirits.values()].reduce((a, b) => a + b, 0) || 1;
    const bottlesTotal = Math.max(spirits.size, Math.ceil((drinks * 1.5) / 25.4));
    const items = [...spirits.entries()].map(([item, w]) => ({
      item,
      qty: Math.max(1, Math.round(bottlesTotal * (w / totalWeight))),
    }));
    cats.push({ name: "Spirits", items });
  }
  if (!isMocktailOnly && liqueurs.size) cats.push({ name: "Liqueurs", items: [...liqueurs].map((item) => ({ item, qty: 1 })) });
  if (!isMocktailOnly && (wine || event?.want_wine)) {
    cats.push({ name: "Wine", items: [
      { item: "Red Wine (750ml)", qty: Math.ceil(adults / 12) || 1 },
      { item: "White Wine (750ml)", qty: Math.ceil(adults / 12) || 1 },
    ]});
  }
  if (!isMocktailOnly && (sparkling || event?.want_champagne)) {
    cats.push({ name: "Champagne / Prosecco", items: [{ item: "Prosecco / Champagne (750ml)", qty: Math.ceil(adults / 8) || 1 }] });
  }
  if (!isMocktailOnly && (beer || event?.want_beer)) {
    cats.push({ name: "Beer / Coolers", items: [{ item: "Beer / Coolers (cans)", qty: Math.ceil(adults * hours * 0.5) || 6 }] });
  }
  return cats;
}

// Back-compat alias — the client-facing alcohol list is alcohol only.
export const generateAlcoholList = generateClientAlcoholList;

// Decide whether a CLIENT alcohol list applies, based on service type + selected cocktails.
// Returns { applicable, reason }. Admin can always override manually.
export function alcoholListApplicability(event) {
  const e = event || {};
  const st = String(e.service_type || "").toLowerCase();
  const pt = String(e.package_type || "").toLowerCase();
  const et = String(e.event_type || "").toLowerCase();
  const hasCocktails = (e.selected_cocktails || []).some((s) => String(s).trim());
  if (st.includes("mocktail"))
    return { applicable: false, reason: "Mocktail-only service — no client alcohol list. Internal ingredients & supplies list only." };
  if (et.includes("kids") || et.includes("lemonade") || pt.includes("kids") || pt.includes("lemonade"))
    return { applicable: false, reason: "Kids lemonade bar — no client alcohol list. Internal ingredients & supplies list only." };
  if (e.need_mobile_bar_rental && !e.need_full_service && !e.need_bartender_only && !pt.includes("full"))
    return { applicable: false, reason: "Alcohol list not required for mobile bar rental only." };
  if (e.need_bartender_only && !e.need_full_service && !pt.includes("full"))
    return hasCocktails
      ? { applicable: true, reason: "" }
      : { applicable: false, reason: "Alcohol list not required for this service type unless cocktails are selected." };
  return hasCocktails
    ? { applicable: true, reason: "" }
    : { applicable: false, reason: "Alcohol list not required unless cocktails are selected." };
}

// ---- Internal ingredients & supplies (NON-alcohol) — never shown to the client ----
export function generateInternalSupplies(event) {
  const adults = num(event?.adult_count) || num(event?.guest_count);
  const guests = num(event?.guest_count) || adults;
  const kids = num(event?.kids_count);
  const cats = [];
  cats.push({ name: "Mixers", items: [
    { item: "Soda Water (2L)", qty: Math.ceil(adults / 10) || 1 },
    { item: "Ginger Beer (cans)", qty: Math.ceil(adults / 4) || 4 },
    { item: "Tonic Water (bottles)", qty: 2 },
    { item: "Cranberry Juice (2L)", qty: 1 },
  ]});
  cats.push({ name: "Citrus & Fruit", items: [
    { item: "Limes", qty: Math.ceil(adults * 0.6) || 10 },
    { item: "Lemons", qty: Math.ceil(adults * 0.3) || 6 },
    { item: "Oranges", qty: Math.ceil(adults * 0.2) || 4 },
    { item: "Fresh Berries / Seasonal Fruit", qty: 2 },
  ]});
  cats.push({ name: "Syrups & Rim", items: [
    { item: "Simple Syrup (bottle)", qty: 1 },
    { item: "Agave Syrup (bottle)", qty: 1 },
    { item: "Tajín / Chamoy (rim)", qty: 1 },
    { item: "Salt / Sugar (rim)", qty: 1 },
  ]});
  cats.push({ name: "Juices", items: [
    { item: "Pineapple Juice (2L)", qty: 1 },
    { item: "Orange Juice (2L)", qty: 1 },
  ]});
  cats.push({ name: "Garnishes & Herbs", items: [
    { item: "Fresh Mint (bunches)", qty: 2 },
    { item: "Jalapeños", qty: 1 },
    { item: "Cocktail picks / umbrellas", qty: 1 },
  ]});
  if (kids > 0 || event?.need_kids_drinks) {
    cats.push({ name: "Kids Drinks", items: [
      { item: "Lemonade (2L)", qty: Math.ceil(kids / 6) || 2 },
      { item: "Sodas / Sparkling (bottles)", qty: Math.ceil(kids / 4) || 3 },
      { item: "Grenadine (Shirley Temple)", qty: 1 },
    ]});
  }
  cats.push({ name: "Cups, Ice & Consumables", items: [
    { item: "12 oz clear plastic cups", qty: guests * 2 || 50 },
    { item: "Napkins", qty: guests * 2 || 50 },
    { item: "Straws", qty: guests * 2 || 50 },
    { item: "Bags of Ice (10lb)", qty: Math.ceil(adults / 10) || 2 },
  ]});
  cats.push({ name: "Bar Tools", items: [
    { item: "Shakers, jiggers, strainers, spoons", qty: 1 },
    { item: "Muddlers, pour spouts, cutting board, knife", qty: 1 },
  ]});
  return cats;
}
